import { useEffect, useState } from "react";
import { api, HR_ROLES, STATUS_COLORS } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM, fmtDate } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { full_name: "", email: "", phone: "", position: "", department: "", branch: "", basic_salary: "", status: "PROBATION", join_date: "" };

export default function Employees() {
  const { user } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [rows, setRows] = useState([]);
  const [org, setOrg] = useState({ branches: [], departments: [] });
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const canEdit = HR_ROLES.includes(user?.role);

  const load = () => {
    api.get("/employees", { params: { search, department: dept === "all" ? "" : dept } }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [search, dept]);
  useEffect(() => { api.get("/org").then((r) => setOrg(r.data)); }, []);

  const submit = async () => {
    try {
      await api.post("/employees", { ...form, basic_salary: parseFloat(form.basic_salary) || 0 });
      toast.success(bm ? "Pekerja ditambah" : "Employee added");
      setAddOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="employees-page" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Pekerja" : "Employees"}</h1>
        {canEdit && (
          <Button data-testid="add-employee-button" className="bg-blue-800 hover:bg-blue-900" onClick={() => setAddOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> {bm ? "Tambah Pekerja" : "Add Employee"}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="employee-search-input" className="pl-9" placeholder={bm ? "Cari nama / ID / jawatan..." : "Search name / ID / position..."}
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger data-testid="employee-dept-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{bm ? "Semua Jabatan" : "All Departments"}</SelectItem>
            {org.departments.map((d) => <SelectItem key={d.department_id} value={d.name}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="bg-card border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{bm ? "Pekerja" : "Employee"}</TableHead>
              <TableHead className="hidden md:table-cell">{bm ? "Jawatan" : "Position"}</TableHead>
              <TableHead className="hidden lg:table-cell">{bm ? "Jabatan" : "Department"}</TableHead>
              <TableHead className="hidden lg:table-cell">{bm ? "Cawangan" : "Branch"}</TableHead>
              <TableHead>{bm ? "Status" : "Status"}</TableHead>
              <TableHead className="text-right">{bm ? "Gaji Pokok" : "Basic Salary"}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.employee_id} data-testid={`employee-row-${e.emp_no}`} className="cursor-pointer" onClick={() => setSelected(e)}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8"><AvatarFallback className="bg-slate-200 text-slate-700 text-xs">{e.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                    <div>
                      <p className="font-medium text-sm">{e.full_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{e.emp_no}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm">{e.position}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{e.department}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{e.branch}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[e.status] || ""}>{e.status}</Badge></TableCell>
                <TableCell className="text-right font-mono text-sm">{typeof e.basic_salary === "number" ? fmtRM(e.basic_salary) : "***"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="employee-detail-dialog" aria-describedby={undefined}>
          {selected && (
            <>
              <DialogHeader><DialogTitle>{selected.full_name}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[
                  [bm ? "ID Pekerja" : "Employee ID", selected.emp_no],
                  [bm ? "Jawatan" : "Position", selected.position],
                  [bm ? "Jabatan" : "Department", selected.department],
                  [bm ? "Cawangan" : "Branch", selected.branch],
                  ["Email", selected.email],
                  [bm ? "Telefon" : "Phone", selected.phone],
                  [bm ? "Tarikh Sertai" : "Join Date", fmtDate(selected.join_date)],
                  [bm ? "Status" : "Status", selected.status],
                  ["EPF No", selected.epf_number],
                  ["SOCSO No", selected.socso_number],
                  [bm ? "Bank" : "Bank", selected.bank_name],
                  [bm ? "Gaji Pokok" : "Basic Salary", typeof selected.basic_salary === "number" ? fmtRM(selected.basic_salary) : "***"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">{k}</p>
                    <p className="font-medium mt-0.5">{v || "-"}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-employee-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{bm ? "Tambah Pekerja Baharu" : "Add New Employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[["full_name", bm ? "Nama Penuh" : "Full Name"], ["email", "Email"], ["phone", bm ? "Telefon" : "Phone"], ["position", bm ? "Jawatan" : "Position"], ["basic_salary", bm ? "Gaji Pokok (RM)" : "Basic Salary (RM)"], ["join_date", bm ? "Tarikh Sertai" : "Join Date"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs">{label}</Label>
                <Input data-testid={`add-emp-${k}`} type={k === "join_date" ? "date" : k === "basic_salary" ? "number" : "text"}
                  value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Jabatan" : "Department"}</Label>
              <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{org.departments.map((d) => <SelectItem key={d.department_id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Cawangan" : "Branch"}</Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>{org.branches.map((b) => <SelectItem key={b.branch_id} value={b.name}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="add-employee-submit" className="mt-4 w-full bg-blue-800 hover:bg-blue-900" onClick={submit}>
            {bm ? "Simpan Pekerja" : "Save Employee"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
